import clsx from 'clsx';

import { ButtonLink } from '@/components/Buttons';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { ParsedUserProduct } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';
import {
  selectHasExplorerFeature,
  selectHasGuidedExperienceFeature,
  selectHasSingletonFeature,
} from '@/redux/selectors/users';

import UpsellPromotedProductSection from './UpsellPromotedProductSection';
import UserProductImage from './UserProductImage';
import { useUserProductDisplay } from './useUserProductDisplay';
import {
  DETAILS_SECTION_CLASS,
  getProductDescription,
  getProductPrice,
  getProductTitle,
  resolveUpsellProduct,
} from './utils';

interface FallbackProductDetailsProps {
  userProduct: ParsedUserProduct;
  activeUserProducts: ParsedUserProduct[];
}

/** Catch-all detail view for non-managed purchases (bundling, giftup, lifetime explorer, day passes). */
const FallbackProductDetails = ({ userProduct, activeUserProducts }: FallbackProductDetailsProps) => {
  const { source } = userProduct.product;
  const { imageURL, title: displayTitle, variant, displayTitleName, habitatId } = useUserProductDisplay(userProduct);
  const currentProductPrice = source === 'stripe' ? getProductPrice(userProduct.product.price) : undefined;

  const [now] = useLatestActiveTime();
  const title = getProductTitle(userProduct.product?.productLookupKey, userProduct.product?.name, displayTitleName);
  const description = getProductDescription(userProduct, now);

  const hasGuidedExperience = useSelector(selectHasGuidedExperienceFeature);
  const hasSingletonFeature = useSelector(selectHasSingletonFeature);
  const hasExplorerFeature = useSelector(selectHasExplorerFeature);

  const isBundling = source === 'bundling';

  const upsellType = resolveUpsellProduct({
    userProduct,
    activeUserProducts,
    hasGuidedExperience,
    hasSingletonFeature,
    hasExplorerFeature,
  });

  return (
    <div>
      <div className={clsx(DETAILS_SECTION_CLASS)}>
        <div className="preflight flex w-full items-center justify-between py-5 mobile:px-3">
          <div className="flex w-full min-w-0 items-center gap-3 mobile:flex-col mobile:items-start">
            <div className="flex items-center gap-3">
              <UserProductImage
                className="size-11 shrink-0 desktop:size-[60px]"
                imageURL={imageURL}
                title={displayTitle}
                variant={variant}
              />
              <h6 className="preflight preflight-h6 line-clamp-2 text-lg font-bold text-neutral-600 desktop:hidden">
                {title}
              </h6>
            </div>
            <div className="w-full min-w-0">
              <h6 className="preflight preflight-h6 line-clamp-2 text-2xl font-bold text-neutral-600 mobile:hidden">
                {title}
              </h6>
              <div className="flex w-full justify-between">
                <p className="preflight preflight-p mt-1 text-base text-stone-500 desktop:text-xl">{description}</p>
                {currentProductPrice ? (
                  <div className="shrink-0 text-lg text-stone-500 desktop:hidden">{currentProductPrice}</div>
                ) : (
                  isBundling && (
                    <ButtonLink
                      href="mailto:support@zoolife.tv"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outline"
                      className="shrink-0 desktop:hidden"
                    >
                      Questions?
                    </ButtonLink>
                  )
                )}
              </div>
            </div>
          </div>
          {currentProductPrice ? (
            <div className="shrink-0 text-xl text-stone-500 mobile:hidden">{currentProductPrice}</div>
          ) : (
            isBundling && (
              <ButtonLink
                href="mailto:support@zoolife.tv"
                target="_blank"
                rel="noopener noreferrer"
                variant="outline"
                className="min-w-40 shrink-0 mobile:hidden"
              >
                Questions?
              </ButtonLink>
            )
          )}
        </div>
      </div>
      {upsellType && <UpsellPromotedProductSection upsellType={upsellType} habitatId={habitatId} />}
    </div>
  );
};

export default FallbackProductDetails;
